import { useComposedRefs } from '@radix-ui/react-compose-refs';
import {
  type ComponentProps,
  forwardRef,
  memo,
  type PointerEvent,
  type RefObject,
  useCallback,
  useMemo,
  useRef,
} from 'react';

import { cn } from '@air/utils-tailwind';

import { usePlayerSeekChange } from '../hooks/usePlayerSeekChange';
import { usePlayerStore } from './PlayerContextProvider';

export type PlayerSeekbarProps = ComponentProps<'div'> & {
  seekTargetRef?: RefObject<HTMLElement | null>;
};

const PERCENTAGE_MULTIPLIER = 100;
const HORIZONTAL_SCROLLBAR_GUTTER_PX = 8;

export const PlayerSeekbar = memo(
  forwardRef<HTMLDivElement, PlayerSeekbarProps>(
    (
      {
        children,
        className,
        onPointerMove,
        onPointerDown,
        onPointerUp,
        onPointerCancel,
        seekTargetRef,
        ...restOfProps
      },
      forwardedRef,
    ) => {
      const { onPlayerSeekChange, onPlayerSeekEnd, onPlayerSeekStart } = usePlayerSeekChange();
      const currentTime = usePlayerStore((state) => state.currentTime);
      const duration = usePlayerStore((state) => state.duration);
      const containerRef = useRef<HTMLDivElement>(null);
      const isDraggingRef = useRef(false);
      const composedRefs = useComposedRefs(forwardedRef, containerRef);

      const handleSeek = useCallback(
        (clientX: number) => {
          if (!containerRef.current || duration <= 0) {
            return;
          }

          const seekTarget = seekTargetRef?.current;
          const target = seekTarget ?? containerRef.current;
          const rect = target.getBoundingClientRect();
          const seekableWidth = Math.max(target.scrollWidth, rect.width);
          const x = clientX - rect.left + (seekTarget ? 0 : containerRef.current.scrollLeft);
          const percentage = Math.max(0, Math.min(1, x / seekableWidth));
          const newTime = percentage * duration;

          onPlayerSeekChange(newTime);
        },
        [duration, onPlayerSeekChange, seekTargetRef],
      );

      const handlePointerDown = useCallback(
        (event: PointerEvent<HTMLDivElement>) => {
          const rect = event.currentTarget.getBoundingClientRect();
          const hasHorizontalScrollbar = event.currentTarget.scrollWidth > event.currentTarget.clientWidth;
          const isScrollbarGutterClick = event.clientY >= rect.bottom - HORIZONTAL_SCROLLBAR_GUTTER_PX;

          // Native scrollbar interactions bubble through the seekbar container; don't treat them as seeks.
          if (hasHorizontalScrollbar && isScrollbarGutterClick) {
            onPointerDown?.(event);
            return;
          }

          const isRightClick = event.button === 2;
          const isLeftClick = event.button === 0;

          if (isRightClick) {
            handleSeek(event.clientX);
            onPointerDown?.(event);
            return;
          }

          if (!isLeftClick) {
            onPointerDown?.(event);
            return;
          }

          if (!isDraggingRef.current) {
            isDraggingRef.current = true;
            event.currentTarget.setPointerCapture(event.pointerId);
            onPlayerSeekStart();
            handleSeek(event.clientX);
          }

          onPointerDown?.(event);
        },
        [handleSeek, onPlayerSeekStart, onPointerDown],
      );

      const handlePointerMove = useCallback(
        (event: PointerEvent<HTMLDivElement>) => {
          if (isDraggingRef.current) {
            handleSeek(event.clientX);
          }

          onPointerMove?.(event);
        },
        [handleSeek, onPointerMove],
      );

      const handlePointerUp = useCallback(
        (event: PointerEvent<HTMLDivElement>) => {
          if (isDraggingRef.current) {
            event.currentTarget.releasePointerCapture(event.pointerId);
            isDraggingRef.current = false;
            onPlayerSeekEnd();
          }

          onPointerUp?.(event);
        },
        [onPlayerSeekEnd, onPointerUp],
      );

      const handlePointerCancel = useCallback(
        (event: PointerEvent<HTMLDivElement>) => {
          handlePointerUp(event);

          onPointerCancel?.(event);
        },
        [handlePointerUp, onPointerCancel],
      );

      const progress = useMemo(() => {
        if (duration <= 0) {
          return 0;
        }

        return (currentTime / duration) * PERCENTAGE_MULTIPLIER;
      }, [currentTime, duration]);

      return (
        <div
          className={cn(
            'group/seekbar relative flex size-full shrink-0 cursor-pointer select-none items-end',
            className,
          )}
          data-progress={progress}
          data-testid="PLAYER_SEEKBAR"
          onPointerCancel={handlePointerCancel}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          {...restOfProps}
          ref={composedRefs}
        >
          {children}
        </div>
      );
    },
  ),
);

PlayerSeekbar.displayName = 'PlayerSeekbar';
